import { useEffect, useMemo, useState } from "react";
import { Download, Search } from "lucide-react";
import { api } from "../services/api";
import CmsEditableText from "../components/cms/CmsEditableText";
import CmsEditBar from "../components/cms/CmsEditBar";
import { useCmsPage } from "../hooks/useCmsPage";
import partBookData from "../data/extracted_table_data.json";
import "./PartBooks.css";

const searchableFields = ["series", "model", "description", "date", "file_size"];

function normalizePartBook(record = {}, index = 0) {
  const modelLinks = record.model_links || record.modelLinks || [];
  return {
    ...record,
    _id: record._id || `fallback-${index}`,
    file_size: record.file_size || record.fileSize || "",
    sort_order: Number(record.sort_order || record.sortOrder || index + 1),
    model_links: modelLinks.map((link) => ({
      ...link,
      source_url: link.source_url || link.sourceUrl || "",
    })),
  };
}

const fallbackPartBookRecords = (partBookData.records || []).map(normalizePartBook);

function normalizeText(value) {
  return String(value || "").toLowerCase();
}

function getRecordSearchText(record) {
  const linkText = (record.model_links || [])
    .map((link) => `${link.title || ""} ${link.url || ""}`)
    .join(" ");

  return [...searchableFields.map((field) => record[field]), linkText]
    .map(normalizeText)
    .join(" ");
}

function PartBooks() {
  const cms = useCmsPage("part-book", {
    header: {
      eyebrow: "Parts book library",
      headline: "Part Book PDF Downloads",
      subtitle: "Download machine parts books and manuals from the local PDF archive.",
    },
  });
  const [query, setQuery] = useState("");
  const [records, setRecords] = useState(fallbackPartBookRecords);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let mounted = true;

    api
      .get("/part-books?limit=1000")
      .then((data) => {
        if (!mounted) return;
        const nextRecords = (data.partBooks || data.records || []).map(normalizePartBook);
        setRecords(nextRecords.length ? nextRecords : fallbackPartBookRecords);
        setLoadError("");
      })
      .catch((error) => {
        if (!mounted) return;
        setRecords(fallbackPartBookRecords);
        setLoadError(error.message);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const filteredRecords = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return records;

    return records.filter((record) => getRecordSearchText(record).includes(term));
  }, [query, records]);

  const totalLinks = useMemo(
    () => records.reduce((sum, record) => sum + (record.model_links || []).length, 0),
    [records]
  );

  return (
    <section className="part-books-page">
      <CmsEditBar editMode={cms.editMode} pageTitle="Part Book Page" />
      <div className="part-books-container">
        <div className="part-books-header">
          <div>
            <CmsEditableText as="p" className="part-books-eyebrow" editMode={cms.editMode} fieldKey="eyebrow" onSave={cms.updateField} sectionKey="header" value={cms.text("header", "eyebrow", "Parts book library")} />
            <CmsEditableText as="h1" editMode={cms.editMode} fieldKey="headline" onSave={cms.updateField} sectionKey="header" value={cms.text("header", "headline", "Part Book PDF Downloads")} />
            <CmsEditableText as="p" className="part-books-intro" editMode={cms.editMode} fieldKey="subtitle" multiline onSave={cms.updateField} sectionKey="header" value={cms.text("header", "subtitle", "Download machine parts books and manuals from the local PDF archive.")} />
          </div>
          <div className="part-books-stats" aria-label="Part book totals">
            <strong>{records.length}</strong>
            <span>table records</span>
            <strong>{totalLinks}</strong>
            <span>PDF links</span>
          </div>
        </div>

        <div className="part-books-toolbar">
          <label className="part-books-search">
            <Search size={18} aria-hidden="true" />
            <span className="sr-only">Search part books</span>
            <input
              type="search"
              placeholder="Search by series, model, description, or file name"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <span className="part-books-result-count">
            {loading ? "Loading library" : `Showing ${filteredRecords.length} of ${records.length}`}
          </span>
        </div>

        {loadError && (
          <p className="part-books-fallback-note">
            Database connection unavailable. Showing bundled part-book data.
          </p>
        )}

        <div className="part-books-table-wrap">
          <table className="part-books-table">
            <thead>
              <tr>
                <th scope="col">Series</th>
                <th scope="col">Model / PDF</th>
                <th scope="col">Description</th>
                <th scope="col">Date</th>
                <th scope="col">Pages</th>
                <th scope="col">File Size</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record, index) => (
                <tr key={record._id || `${record.series}-${record.model}-${index}`}>
                  <td data-label="Series">{record.series}</td>
                  <td data-label="Model / PDF">
                    <div className="part-books-model">{record.model}</div>
                    <div className="part-books-links">
                      {(record.model_links || []).map((link) => (
                        <a
                          className="part-books-pdf-link"
                          href={link.url}
                          key={`${link.title}-${link.url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Download size={15} aria-hidden="true" />
                          <span>{link.title || "Open PDF"}</span>
                        </a>
                      ))}
                    </div>
                  </td>
                  <td data-label="Description">{record.description}</td>
                  <td data-label="Date">{record.date}</td>
                  <td data-label="Pages">{record.pages}</td>
                  <td data-label="File Size">{record.file_size}</td>
                </tr>
              ))}
              {filteredRecords.length === 0 && (
                <tr>
                  <td className="part-books-empty" colSpan="6">
                    No part book PDFs match that search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default PartBooks;
